import React, { useState, useEffect, useCallback } from "react";
import Cookies from "universal-cookie";
import { CompleteLoader } from '../../modals/common-alert';
import { useSelector, useDispatch } from 'react-redux';
import { getHostHelperContract } from '../../helper/contract-helper';
import { toast, Bounce } from 'react-toastify';
import { transactionAction } from '../../helper/chain-helper';
import { getProfileSettings, getSendAsEmails } from '../../helper/settingsHelper.js';
import Loader from "../../components/Loader.js";
import Web3 from 'web3';
import web3Config from "../../web3/web3Config.json";
import AddEmailModal from "../../modals/AddEmailModal.js";

const cookies = new Cookies();

const SettingRow = ({ label, description, children, minWidth = '130px' }) => (
    <div className="d-flex flex-column flex-xl-row align-items-xl-center gap-2 gap-md-4 setting-cards border-bottom-F0F0F0">
        <div style={{ minWidth }}>
            <h3 className="setting-labels">{label}</h3>
            {description && <h5>{description}</h5>}
        </div>
        <div className="d-flex align-items-center gap-2 flex-grow-1">{children}</div>
    </div>
);

const Profile = () => {
    const [loader, setLoader] = useState(false);
    const [user] = useState(cookies.get("userObject"));
    const [account, setAccount] = useState('');
    const [web3Value, setWeb3] = useState(null);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [sectionLoader, setSectionLoader] = useState(false);
    const dispatch = useDispatch();
    const refreshSettings = useSelector((state) => state.refreshSettings);
    const [errors, setError] = useState({});
    const [text, setText] = useState("");
    const [emailActionLoading, setEmailActionLoading] = useState(false);


    const [emails, setEmails] = useState([]);
    const [replyOption, setReplyOption] = useState('default');
    const [addAnotherUserModal, setAddAnotherUserModal] = useState(false);
    const [verifyEmail, setVerifyEmail] = useState('');

    const fetchdata = useCallback(async () => {
        setSectionLoader(true);
        const profileSettings = await getProfileSettings();
        const sendAsEmail = await getSendAsEmails();
        setEmails([{ email: user.name, isDefault: true, isOwn:true }, ...sendAsEmail]);
        dispatch({ type: "PROFILE_NAME", payload: profileSettings });
        setFirstName(profileSettings.firstName);
        setLastName(profileSettings.lastName);
        setSectionLoader(false);
    }, [dispatch]);

    useEffect(() => {
        if (window.ncogProvider) {
            const web3Instance = new Web3(web3Config?.CHAIN_DETAILS?.rpcUrls[0]);
            setWeb3(web3Instance);
            const walletAddress = localStorage.getItem("walletAddress");
            setAccount(walletAddress);
        } else {
            console.log('NCOG wallet is not installed');
        }
        fetchdata();
    }, [fetchdata, refreshSettings]);

    const handleFirstName = (e) => {
        setFirstName(e.target.value);
    };

    const handleLastName = (e) => {
        setLastName(e.target.value);
    };

    const saveProfile = async () => {
        const namePattern = /^[A-Za-z ]+$/;
        let validationErrors = {};

        if (!firstName) {
            validationErrors.firstName = 'First name is required';
        } else if (!namePattern.test(firstName)) {
            validationErrors.firstName = 'First name can only contain letters and spaces';
        } else if (firstName.length < 3) {
            validationErrors.firstName = 'First name must be at least 3 characters long';
        }

        if (!lastName) {
            validationErrors.lastName = 'Last name is required';
        } else if (!namePattern.test(lastName)) {
            validationErrors.lastName = 'Last name can only contain letters and spaces';
        } else if (lastName.length < 3) {
            validationErrors.lastName = 'Last name must be at least 3 characters long';
        }

        if (Object.keys(validationErrors).length > 0) {
            setError(validationErrors);
            return;
        }
        setError({});
        setLoader(true);
        setText("Please wait while we securely store your data on the blockchain, This will be faster next time.");
        const options = [
            { key: "firstName", value: firstName },
            { key: "lastName", value: lastName }
        ];

        const helperContract = getHostHelperContract();
        const functionParams = [user.name, 'profile', options];
        const hash = await transactionAction(helperContract, "updateSettings", functionParams, account, 'helper');
        if (hash) {
            dispatch({ type: "REFRESH_SETTINGS", payload: !refreshSettings });
            toast.success("Successfully updated.", {
                position: "top-center",
                transition: Bounce,
            });
        } else {
            toast.error("Something went wrong.", {
                position: "top-center",
                transition: Bounce,
            });
        }
        await fetchdata();
        setLoader(false);
        setText("");
    };

    const hanldeOnCloseModal = () => {
        setVerifyEmail('');
        setAddAnotherUserModal(false);
    }

    const updateSendAsEmails = async (newEmails) => {
        setEmailActionLoading(true);
        const options = [];
        newEmails.forEach((email, index) => {
            options.push(
                { key: `email~${index + 1}`, value: email.email },
                { key: `isDefault~${index + 1}`, value: email.isDefault === true || email.isDefault === 'true' ? "true" : "false" },
                { key: `verify~${index + 1}`, value: email.verify === true || email.verify === 'true' ? "true" : "false" }
            );
        });
        const helperContract = getHostHelperContract();
        const user = cookies.get("userObject");
        const functionParams = [user.name, 'sendAsEmail', options];
        const hash = await transactionAction(helperContract, "updateSettings", functionParams, '', 'helper');
        setEmailActionLoading(false);
        if (hash) {
            dispatch({ type: "REFRESH_SETTINGS", payload: !refreshSettings });
            toast.success("Successfully updated.", {
                position: "top-center",
                transition: Bounce,
            });
        } else {
            toast.error("Something went wrong.", {
                position: "top-center",
                transition: Bounce,
            });
        }
    };

    // Set Default
    const handleMakeDefault = async (idx) => {
        const sendAsEmail = await getSendAsEmails();
        const newEmails = sendAsEmail.map((email, i) => ({
            ...email,
            isDefault: (i + 1) === idx ? "true" : "false"
        }));
        await updateSendAsEmails(newEmails);
        setEmails(newEmails);
    };

    // Verify
    const handleVerify = async (email) => {
        setVerifyEmail(email);
        setAddAnotherUserModal(true);
    };

    // Delete
    const handleDelete = async (idx) => {
        const sendAsEmail = await getSendAsEmails();
        const newEmails = sendAsEmail.filter((_, i) => (i + 1) !== idx);
        await updateSendAsEmails(newEmails);
        setEmails(newEmails);
    };

    const onAddEmail = () => {
        setVerifyEmail('');
        setAddAnotherUserModal(true)
    };


    const displayEmail = emails?.find(email => email.isDefault === 'true')?.email;
    emails?.forEach(email => {
        email.isDefault = email.email === displayEmail ? 'true' : 'false';
    })
    
    if (!displayEmail) {
        emails.forEach(email => {
            email.isDefault = email.email === user.name ? 'true' : 'false';
        })
    }

    return (
        <>
            {!sectionLoader ? (
                <>
                    <SettingRow label="Account :">
                        <div className="d-flex flex-column flex-xl-row align-items-xl-center gap-3">
                            <div className="d-flex align-items-center gap-3">
                                <h4 className="input-label d-none d-md-block">First Name</h4>
                                <div key="firstName">
                                    <input
                                        type="text"
                                        placeholder="First Name"
                                        className="multiSelectOption-items"
                                        value={firstName}
                                        onChange={handleFirstName}
                                    />
                                    {errors?.firstName && <div className="error-message">{errors?.firstName}</div>}
                                </div>
                            </div>
                            <div className="d-flex align-items-center gap-3">
                                <h4 className="input-label d-none d-md-block">Last Name</h4>
                                <div key="lastName">
                                    <input
                                        type="text"
                                        placeholder="Last Name"
                                        className="multiSelectOption-items"
                                        value={lastName}
                                        onChange={handleLastName}
                                    />
                                    {errors?.lastName && <div className="error-message">{errors?.lastName}</div>}
                                </div>
                            </div>
                        </div>
                    </SettingRow>
                    {/* <SettingRow label="Send mail as:">
                        <div className="container-fluid p-0" style={{fontSize: '12px'}}>
                            <div className="row mb-4">
                                <div className="col-12">
                                    {emails?.map((item, idx) => (
                                        <div key={item.email} className="d-flex align-items-center py-2 border-bottom">
                                            <div className="d-flex align-items-center flex-grow-1">
                                                <span className={item.isDefault ? 'fw-semibold' : ''}>
                                                {item.email}
                                                </span>
                                                { ((item.isDefault === 'true') || (item.isDefault === true)) && (
                                                    <span className="text-secondary ms-2">default</span>
                                                )}
                                            </div>
                                            <div className="d-flex gap-3">
                                                {item.isDefault === 'false' && item.verify !== 'false' && (
                                                    <button
                                                        style={{ fontSize: '12px' }}
                                                        className="btn btn-link p-0 text-decoration-none"
                                                        onClick={() => handleMakeDefault(idx)}
                                                        disabled={emailActionLoading}
                                                    >
                                                        Make default
                                                    </button>
                                                )}
                                                {item.verify === 'false' && (
                                                    <button
                                                        style={{ fontSize: '12px' }}
                                                        className="btn btn-link p-0 text-decoration-none"
                                                        onClick={() => handleVerify(item.email)}
                                                        disabled={emailActionLoading}
                                                    >
                                                        Verify
                                                    </button>
                                                )}
                                                {!item.isOwn && (
                                                    <button
                                                        style={{ fontSize: '12px' }}
                                                        className="btn btn-link p-0 text-decoration-none"
                                                        onClick={() => handleDelete(idx)}
                                                        disabled={emailActionLoading}
                                                    >
                                                        Delete
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    <div className="text-start py-2">
                                        <button
                                            style={{ fontSize: '12px' }}
                                            className="btn btn-link text-decoration-none p-0"
                                            onClick={onAddEmail}
                                        >
                                            Add another email address
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="row mt-4">
                                <div className="col-12">
                                    <div className="fw-semibold mb-3">When replying to a message:</div>
                                    <div className="form-check mb-2">
                                        <input
                                            type="radio"
                                            name="replyOption"
                                            id="replySame"
                                            className="form-check-input"
                                            value="same"
                                            checked={replyOption === 'same'}
                                            onChange={() => setReplyOption('same')}
                                        />
                                        <label className="form-check-label" htmlFor="replySame">
                                            Reply from the same address to which the message was sent
                                        </label>
                                    </div>
                                    <div className="form-check mb-2">
                                        <input
                                            type="radio"
                                            name="replyOption"
                                            id="replyDefault"
                                            className="form-check-input"
                                            value="default"
                                            checked={replyOption === 'default'}
                                            onChange={() => setReplyOption('default')}
                                        />
                                        <label className="form-check-label" htmlFor="replyDefault">
                                            Always reply from default address (currently {emails.find(e => e.isDefault)?.email})
                                        </label>
                                    </div>
                                    <div className="text-secondary small mt-2">
                                        (Note: You can change the address at the time of your reply.)
                                    </div>
                                </div>
                            </div>
                        </div>
                    </SettingRow> */}

                    <div className='save-settings-btn mt-5' onClick={saveProfile}>
                        <button>
                            <span>Save Changes</span>
                        </button>
                    </div>
                </>
            ) : (
                <Loader normal={true} />
            )}
            <CompleteLoader isOpen={loader || emailActionLoading} text={text || (emailActionLoading ? 'Processing email action...' : '')} />
            <AddEmailModal open={addAnotherUserModal} onClose={hanldeOnCloseModal} isVerify={verifyEmail} verifyEmail={verifyEmail} />
        </>
    );
};

export default Profile;